
import { mapState } from 'pinia';
import { SHOP_HERO_QUERY } from '@Settings/catalog.settings';
import {
  PLP_ARRIVALS,
  PLP_PROMOTIONS,
  PLP_SHOP,
} from '@Settings/listing.settings';
import { useUser } from '@Stores/user';
import errorHandler from '@Utils/error-handler';
import { adapterCatalogHero } from '@Adapters/cms/categories.adapter';
import { adapterCategoriesAnimation } from '@Adapters/catalog/categories.adapter';
import { componentPageDisplay } from '@Utils/analytics.events';
import { getCategoryUrl, getShopCategory } from '@Utils/catalog';
import SeoDataMixin from '@Mixins/seo-data';
import { adapterRetailerMessage } from '@Adapters/cms/messages.adapter';

export default {
  name: 'ProductShopPage',
  mixins: [SeoDataMixin],
  middleware({ localeLocation, redirect, route }) {
    if (route.matched[0].props?.default?.redirect) {
      const query = {
        searchTerm: route.query?.searchTerm,
      };
      if (route.matched[0].props?.default?.animation) {
        query[SHOP_HERO_QUERY] = 'true';
      }

      return redirect(
        301,
        localeLocation({
          name: 'product-shop',
          params: {
            category: route.params.category,
          },
          query,
        })
      );
    }
  },
  async asyncData(context) {
    const { $pinia, $publicAPI, error, i18n, localeLocation, redirect, route } =
      context;
    const needHeroFetch = route.query?.[SHOP_HERO_QUERY];
    const categoryId = getShopCategory(route.params.category);
    let category, categoryAnimation, config, hero, message;
    let fetched = false;

    const searchTerm = route.query?.searchTerm;
    const animation = route.query?.[SHOP_HERO_QUERY];

    switch (categoryId) {
      case 'promotion':
        config = PLP_PROMOTIONS;
        category = i18n.t('catalog.shops.promotion');

        break;
      case 'news':
        config = PLP_ARRIVALS;
        category = i18n.t('catalog.shops.news');

        break;
      default:
        config = {
          ...PLP_SHOP,
          endpoint: {
            ...PLP_SHOP.endpoint,
            params: categoryId,
          },
        };

        try {
          category = await $publicAPI.catalog.getCategory(categoryId, {
            depth: 1,
            withSeo: true,
          });

          if (category?.redirectCategoryId) {
            redirect(302, {
              path: getCategoryUrl(category.redirectCategoryId, $pinia),
              query: {
                [SHOP_HERO_QUERY]: animation,
                searchTerm,
              },
            });
            return;
          }

          if (category.animation) {
            categoryAnimation = adapterCategoriesAnimation(
              category.animation,
              i18n
            );
          }
        } catch (err) {
          errorHandler(err, context, {
            logOnly: true,
            path: 'pages.products.shop.asyncData.category',
          });

          if (err.statusCode === 410) {
            redirect(301, localeLocation({ name: 'home' }));
          } else {
            error(err);
          }

          return undefined;
        }

        break;
    }

    if (process.client) {
      try {
        const fetch = [
          $publicAPI.cms.getEntry('bubble_message', {
            params: {
              categoryId,
              targetPage: 'category',
            },
          }),
        ];

        if (needHeroFetch) {
          fetch[1] = $publicAPI.cms.getEntry('category_header', {
            params: {
              categoryId,
            },
          });
        }

        const results = await Promise.allSettled(fetch);
        results.forEach(({ status, reason, value }, index) => {
          if (status === 'rejected') {
            errorHandler(reason, context, {
              logOnly: true,
              path: 'pages.products.shop.asyncData.rejected',
            });
            return;
          }

          if (!value) {
            return;
          }

          switch (index) {
            case 0:
              message = adapterRetailerMessage(value?.[0], context);
              break;
            case 1:
              hero = adapterCatalogHero(value);
              break;
          }
        });
      } catch (err) {
        errorHandler(err, context, {
          logOnly: true,
          path: 'pages.products.shop.asyncData.all',
        });
      }

      fetched = true;
    }

    return {
      animation,
      category,
      categoryAnimation,
      categoryId,
      config,
      fetched,
      hero,
      message,
      needHeroFetch,
      searchTerm,
    };
  },
  data() {
    return {
      animation: false,
      category: undefined,
      categoryId: undefined,
      config: undefined,
      fetched: false,
      hero: undefined,
      message: undefined,
      needHeroFetch: false,
      searchTerm: undefined,
    };
  },
  fetch() {
    this.fetchCampaigns();
  },
  fetchOnServer: false,
  computed: {
    ...mapState(useUser, ['site']),
    listingKey() {
      return `listing-${this.categoryId}-${this.site?.id}-${this.site?.mode}`;
    },
    seo() {
      let { title, description } = this.category?.seo || {};
      let robots;

      if (Object.keys(this.$route.query).length) {
        robots = 'none';
      }

      switch (this.categoryId) {
        case 'promotion':
          title = this.$t('meta.promotions.title');
          description = this.$t('meta.promotions.description');

          break;
        case 'news':
          title = this.$t('meta.arrivals.title');
          description = this.$t('meta.arrivals.description');

          break;
        default:
          title = this.$t('meta.shops.title', {
            label: title,
          });
      }

      return {
        description,
        robots,
        title,
      };
    },
  },
  watch: {
    site() {
      this.updateCategory();
    },
  },
  methods: {
    async fetchCampaigns(force) {
      if (this.fetched && !force) {
        return;
      }

      this.hero = undefined;
      this.message = undefined;

      try {
        const fetch = [
          this.$publicAPI.cms.getEntry('bubble_message', {
            params: {
              categoryId: this.categoryId,
              targetPage: 'category',
            },
          }),
        ];

        if (this.needHeroFetch) {
          fetch[1] = this.$publicAPI.cms.getEntry('category_header', {
            params: {
              categoryId: this.categoryId,
            },
          });
        }

        const results = await Promise.allSettled(fetch);
        results.forEach(({ status, reason, value }, index) => {
          if (status === 'rejected') {
            errorHandler(reason, this, {
              logOnly: true,
              path: 'pages.products.shop.fetchCampaigns.rejected',
            });
            return;
          }

          if (!value) {
            return;
          }

          switch (index) {
            case 0:
              this.message = adapterRetailerMessage(value?.[0], this);
              break;

            case 1:
              this.hero = adapterCatalogHero(value);
              break;
          }
        });
      } catch (err) {
        errorHandler(err, this, {
          logOnly: true,
          path: 'pages.products.shop.fetchCampaigns.all',
        });
      }

      this.fetched = true;
    },
    onLoaded(data) {
      if (process.client) {
        const shopData = {
          id: this.$route.params?.category,
          label: this.category?.label,
        };

        // emit page.display analytics event
        componentPageDisplay(this, { ...shopData, ...data });

        // Little theme tracking
        if (this.$shoppingTools.page === 'homeSelection') {
          this.$shoppingTools.setCampaign({
            onsitead_detailed_placement: 'Shop_Home',
            onsitead_campaign: this.$shoppingTools.info1,
            onsitead_category: this.$shoppingTools.type,
            onsitead_general_placement: 'hp',
          });
        }

        this.$shoppingTools.shopName = shopData;
        this.$shoppingTools.overridePage();
        this.$shoppingTools.overrideOrigin();
      }
    },
    async updateCategory() {
      if (this.categoryId !== 'promotion' && this.categoryId !== 'news') {
        try {
          this.category = await this.$publicAPI.catalog.getCategory(
            this.categoryId,
            {
              depth: 1,
              withSeo: true,
            }
          );

          if (this.category?.redirectCategoryId) {
            this.$router.replace({
              path: getCategoryUrl(
                this.category.redirectCategoryId,
                this.$pinia
              ),
              query: {
                [SHOP_HERO_QUERY]: this.animation,
                searchTerm: this.searchTerm,
              },
            });
            return;
          }

          if (this.category.animation) {
            this.categoryAnimation = adapterCategoriesAnimation(
              this.category.animation,
              this.$i18n
            );
          } else {
            this.categoryAnimation = undefined;
          }
        } catch (err) {
          errorHandler(err, this.$nuxt, {
            logOnly: true,
            path: 'pages.products.shop.updateCategory',
          });

          if (err.statusCode === 410) {
            this.$router.replace(this.localeLocation({ name: 'home' }));
          } else {
            this.$nuxt.error(err);
          }

          return;
        }
      }

      this.fetchCampaigns(true);
    },
  },
};
